/**
 * PayPal return/cancel handler (Monetization v2, sc-6943), classic cabinet.
 * Called from main.ts BEFORE the cabinet boots, mirroring the auth callback:
 *
 *   /billing/return  — buyer APPROVED. Day pass (?token=<orderId>): CAPTURE
 *       the order (collects the money); the PAYMENT.CAPTURE.COMPLETED
 *       webhook grants the pass. Sub (?subscription_id): approval already
 *       fired BILLING.SUBSCRIPTION.ACTIVATED. Either way POLL /v1/wallet
 *       until the entitlement lands (the webhook is async).
 *   /billing/cancel  — buyer bailed. No charge, no grant.
 *
 * The URL is always rewritten back to `/` on completion so a refresh
 * doesn't re-capture a consumed order. The pending cartridge (stashed
 * before the redirect) relaunches via the boot-time consumePendingLaunch()
 * in main.ts — the classic cabinet re-checks the gate, which now sees the
 * active pass and plays unlimited.
 */

import type { Wallet } from '@arcraz/arcadia-types';
import { captureOrder, getWallet } from '../api/client.js';

const RETURN_PATH = '/billing/return';
const CANCEL_PATH = '/billing/cancel';

const WALLET_POLL_ATTEMPTS = 8;
const WALLET_POLL_DELAY_MS = 750;

export interface BillingReturnOutcome {
  /** True if the URL was a billing return/cancel URL. */
  handled: boolean;
  /** True if an active pass was confirmed. */
  entitled: boolean;
}

const sleep = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

async function waitForEntitlement(): Promise<boolean> {
  for (let i = 0; i < WALLET_POLL_ATTEMPTS; i += 1) {
    let wallet: Wallet | null = null;
    try {
      wallet = await getWallet();
    } catch {
      /* transient — try again */
    }
    if (wallet?.entitlement) return true;
    if (i < WALLET_POLL_ATTEMPTS - 1) await sleep(WALLET_POLL_DELAY_MS);
  }
  return false;
}

/**
 * Returns immediately if the URL isn't a billing return/cancel. Otherwise
 * finalizes the purchase and reports whether a pass is now active. Always
 * rewrites the URL back to `/` on completion.
 */
export async function runBillingReturnIfNeeded(): Promise<BillingReturnOutcome> {
  const path = window.location.pathname;

  if (path === CANCEL_PATH) {
    window.history.replaceState(null, '', '/');
    return { handled: true, entitled: false };
  }

  if (path !== RETURN_PATH) {
    return { handled: false, entitled: false };
  }

  const params = new URLSearchParams(window.location.search);
  const orderId = params.get('token');
  const subscriptionId = params.get('subscription_id');

  try {
    if (orderId) {
      // Day pass: capture to collect the money. Tolerate a capture error
      // (already captured on a refresh) — the webhook is the grant source
      // of truth, so we still poll the wallet below.
      try {
        await captureOrder(orderId);
      } catch (err) {
        console.warn('[arcadia84] day-pass capture reported an error', { err });
      }
    } else if (!subscriptionId) {
      console.warn('[arcadia84] billing return missing token/subscription_id');
    }

    const entitled = await waitForEntitlement();
    window.history.replaceState(null, '', '/');
    return { handled: true, entitled };
  } catch (err) {
    console.warn('[arcadia84] billing return failed', { err });
    window.history.replaceState(null, '', '/');
    return { handled: true, entitled: false };
  }
}
